(() => {
  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

  const getStep = (track) => {
    const item = track.querySelector('.shoreline-clearance__item');
    if (!item) return track.clientWidth * 0.5;
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return item.getBoundingClientRect().width + gap;
  };

  const updateNav = (track, prevBtn, nextBtn) => {
    const max = Math.max(0, track.scrollWidth - track.clientWidth - 2);
    const left = track.scrollLeft;
    if (prevBtn) prevBtn.disabled = left <= 2;
    if (nextBtn) nextBtn.disabled = left >= max;
  };

  const initSlider = (panel) => {
    const track = panel.querySelector('[data-clearance-track]');
    const prevBtn = panel.querySelector('[data-clearance-prev]');
    const nextBtn = panel.querySelector('[data-clearance-next]');
    if (!track) return;
    if (track.dataset.clearanceSliderReady === 'true') {
      // Already wired; the cards may just have been lazy-hydrated, so only refresh the
      // arrow state rather than binding a second set of click handlers.
      if (typeof track.__shRefresh === 'function') track.__shRefresh();
      return;
    }
    track.dataset.clearanceSliderReady = 'true';

    const refresh = () => updateNav(track, prevBtn, nextBtn);
    track.__shRefresh = refresh;

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -getStep(track), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: getStep(track), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    refresh();
  };

  const initCountdown = (root) => {
    if (root.dataset.clearanceCountdownReady === 'true') return;
    root.dataset.clearanceCountdownReady = 'true';

    const hrsEl = root.querySelector('[data-countdown-hrs]');
    const minEl = root.querySelector('[data-countdown-min]');
    const secEl = root.querySelector('[data-countdown-sec]');
    if (!hrsEl || !minEl) return;

    const mode = root.getAttribute('data-countdown-mode') || 'local';
    let resetHour = parseInt(root.getAttribute('data-countdown-reset-hour'), 10);
    if (Number.isNaN(resetHour) || resetHour < 0 || resetHour > 23) resetHour = 0;

    // Next occurrence of the reset hour on the SHOPPER'S own clock.
    const nextLocalReset = () => {
      const now = new Date();
      const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), resetHour, 0, 0, 0);
      if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
      return target.getTime();
    };

    let end;
    if (mode === 'fixed') {
      const endAttr = root.getAttribute('data-countdown-end');
      if (!endAttr) return;
      end = new Date(endAttr).getTime();
      if (Number.isNaN(end)) return;
    } else {
      end = nextLocalReset();
    }

    const tick = () => {
      let diff = end - Date.now();
      if (diff <= 0) {
        if (mode === 'fixed') {
          diff = 0;
        } else {
          end = nextLocalReset();
          diff = Math.max(0, end - Date.now());
        }
      }
      const totalSecs = Math.floor(diff / 1000);
      hrsEl.textContent = pad(Math.floor(totalSecs / 3600));
      minEl.textContent = pad(Math.floor(totalSecs / 60) % 60);
      if (secEl) secEl.textContent = pad(totalSecs % 60);
    };

    const interval = secEl ? 1000 : 30000;
    let timer = null;
    const start = () => {
      if (timer) window.clearInterval(timer);
      tick();
      timer = window.setInterval(tick, interval);
    };

    // Resync when the tab comes back: a sleeping device can wake up past the reset.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (timer) window.clearInterval(timer);
        timer = null;
      } else {
        if (mode !== 'fixed') end = nextLocalReset();
        start();
      }
    });

    start();
  };

  const activateTab = (root, tab, { hydrate = true } = {}) => {
    const tabs = root.querySelectorAll('[data-clearance-tab]');
    const panels = root.querySelectorAll('[data-clearance-panel]');
    const id = tab.getAttribute('data-clearance-tab');

    tabs.forEach((t) => {
      const active = t === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
    });

    panels.forEach((panel) => {
      const match = panel.getAttribute('data-clearance-panel') === id;
      if (match) {
        panel.removeAttribute('hidden');
        panel.classList.add('is-active');
        // A hidden tab never intersects the viewport, so build its cards now (no-op if
        // they are already in the DOM). The lazy-hydrated event below refreshes the slider.
        if (hydrate && window.ShorelineLazy) {
          panel.querySelectorAll('[data-sh-lazy]').forEach((el) => window.ShorelineLazy.hydrate(el));
        }
        initSlider(panel);
      } else {
        panel.setAttribute('hidden', '');
        panel.classList.remove('is-active');
      }
    });
  };

  const initClearance = (root) => {
    if (!root || root.dataset.clearanceReady === 'true') return;
    root.dataset.clearanceReady = 'true';

    initCountdown(root);

    const tabs = root.querySelectorAll('[data-clearance-tab]');
    const panels = root.querySelectorAll('[data-clearance-panel]');
    panels.forEach((panel) => initSlider(panel));

    const tablist = root.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-clearance-tab]');
        if (!tab || !root.contains(tab)) return;
        event.preventDefault();
        activateTab(root, tab);
      });
    } else {
      tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          activateTab(root, tab);
        });
      });
    }

    // Initial state only — the visible tab's cards are lazy-hydrated when the section
    // nears the viewport, not on page load.
    const active = root.querySelector('[data-clearance-tab].is-active') || tabs[0];
    if (active) activateTab(root, active, { hydrate: false });
  };

  const initAll = () => {
    document.querySelectorAll('[data-shoreline-clearance]').forEach(initClearance);
  };

  // Cards arrived in a track (window.ShorelineLazy) — recompute that panel's arrows.
  document.addEventListener('shoreline:lazy-hydrated', (event) => {
    const panel = event.target && event.target.closest ? event.target.closest('[data-clearance-panel]') : null;
    if (panel) initSlider(panel);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!section) return;
    const root =
      section.matches?.('[data-shoreline-clearance]')
        ? section
        : section.querySelector?.('[data-shoreline-clearance]');
    if (root) {
      delete root.dataset.clearanceReady;
      delete root.dataset.clearanceCountdownReady;
      root.querySelectorAll('[data-clearance-track]').forEach((track) => {
        delete track.dataset.clearanceSliderReady;
      });
      initClearance(root);
    }
  });
})();
